import { Surface } from '@dapp/multi-lib/sdaoui/Surface';
import { AssetUsdValue } from 'components/AssetUsdValue';
import { ChipAssetChanges } from 'components/ChipAssetChanges';
import { Body2, Title1 } from 'components/Typography';
import { UsdValue } from 'components/UsdValue';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { VaultInfoItem } from 'structure/pv2/vault/components/VaultInfoItem';
import { VaultTokens } from 'structure/pv2/vault/components/VaultTokens';
import {
  useVault,
  useVaultAllocation,
} from 'structure/pv2/vault/hooks/useVault';
import { useDepositTokenPrice } from 'structure/pv2/vault/hooks/useDepositTokenPrice';
import {
  getVaultAPYPrefix,
  getRiskLevelColorClass,
  getRiskLevelLabel,
  VaultIdentity,
} from 'structure/pv2/vault/utils/vault';
import { getTokenIconUrl } from 'utils/url';
import { VaultType } from '../configs/vaultTypes';
import { useConnection } from 'wagmi';
import { useVaultBlockchain } from 'structure/pv2/vault/hooks/useVaultBlockchain';

interface VaultMainInfoProps {
  vaultIdentity: VaultIdentity;
}

export const VaultMainInfo = (props: VaultMainInfoProps) => {
  const { t } = useTranslation('vault');
  const { vaultIdentity } = props;

  const account = useConnection();
  const vaultQuery = useVault(vaultIdentity);
  const vaultBlockchainQuery = useVaultBlockchain({
    ...vaultIdentity,
    query: {
      refetchInterval: 14000,
      staleTime: 14000,
    },
  });

  const depositTokenPriceQuery = useDepositTokenPrice({
    vaultAddress: vaultIdentity.address,
    vaultChainId: vaultIdentity.chainId,
    from: account.address as `0x${string}`,
  });

  const tokenAllocationQuery = useVaultAllocation(vaultIdentity);

  if (vaultQuery.isLoading || !vaultQuery.data) {
    return <Title1>Loading...</Title1>;
  }

  const vault = vaultQuery.data;

  const tokenList = tokenAllocationQuery.data
    ? tokenAllocationQuery.data?.map((item) => ({
        iconSrc: getTokenIconUrl(item.token.symbol),
        symbol: item.token.symbol,
      }))
    : [];

  // Convert vault price (in deposit token) to USD using oracle
  const vaultPriceInUsd =
    vault.price && depositTokenPriceQuery.data?.price
      ? vault.price * depositTokenPriceQuery.data.price
      : vault.price;

  const tvl = vaultBlockchainQuery.data
    ? vaultBlockchainQuery.data.tvlUsd
    : '0';
  const currentReturn =
    vault.type === VaultType.YIELD
      ? Math.min(parseFloat(vault.twentyFourHourAPY?.toString() || '0'), 1000)
      : parseFloat(vault.twentyFourHourPerformance);

  return (
    <Surface rounded='large'>
      <div className='flex flex-col'>
        <Title1 className='mb-6 text-[#D9D9D9]'>{vault.name}</Title1>

        <div className="grid items-center gap-y-6 [grid-template-areas:'price_tvl''price_tokens''type_risk']">
          <div className='flex flex-col self-start [grid-area:price]'>
            <AssetUsdValue value={vaultPriceInUsd} />
            <div className='mt-7'>
              <ChipAssetChanges
                percents={currentReturn}
                prefix={getVaultAPYPrefix(vault, currentReturn)}
              />
            </div>
          </div>

          <div className='[grid-area:tvl]'>
            <VaultInfoItem
              icon='safe'
              label={t('VAULT_TVL')}
              value={<UsdValue value={tvl.toString()} />}
            />
          </div>
          <div className='[grid-area:tokens]'>
            <VaultInfoItem
              icon='generating_tokens'
              label={t('VAULT_TOKEN')}
              value={
                tokenAllocationQuery.isLoading ? (
                  <div>Loading...</div>
                ) : (
                  <VaultTokens
                    contributionTokenSymbol='USDC'
                    tokenList={tokenList}
                  />
                )
              }
            />
          </div>
          <div className='mt-6 [grid-area:type]'>
            <VaultInfoItem
              icon='safe'
              label={t('VAULT_TYPE')}
              value={
                <Body2 className='font-normal lowercase first-letter:uppercase'>
                  {vault.type}
                </Body2>
              }
            />
          </div>
          <div className='mt-6 [grid-area:risk]'>
            <VaultInfoItem
              icon='warning'
              label={
                <div className='flex items-center gap-1 text-white-400'>
                  <span>{t('RISK_LEVEL')}</span>
                </div>
              }
              value={
                <Body2
                  className={`font-normal ${getRiskLevelColorClass(vault.riskLevel)}`}
                >
                  {getRiskLevelLabel(vault.riskLevel)}
                </Body2>
              }
            />
          </div>
        </div>
      </div>
    </Surface>
  );
};
